import type { NetworkProgress, NetworkTrackingCallback } from "../types/Network"

const allowBody = (status: number): boolean =>
  !((status >= 100 && status < 200) || status === 204 || status === 304)

function getHeaders(xhttp: XMLHttpRequest): HeadersInit {
  const headers: Record<string, string> = {}
  const raw = xhttp.getAllResponseHeaders().trim()
  const lines = raw.split(/[\r\n]+/)
  for (const line of lines) {
    const parts = line.split(": ")
    const key = parts.shift()
    if (key) headers[key.toLowerCase()] = parts.join(": ")
  }
  return headers
}

export function ProgressFetchXHR(
  url: string,
  {
    data,
    headers,
    withCredentials,
  }: {
    data?: any
    headers?: HeadersInit
    withCredentials?: boolean
  } = {},
  { download, upload }: NetworkProgress = {}
): Promise<Response> {
  let responseHeaders: HeadersInit | undefined
  let downloadTotal: number | undefined

  const setHeaders = (xhttp: XMLHttpRequest): HeadersInit => {
    if (!responseHeaders) {
      responseHeaders = getHeaders(xhttp)
      const cl = (responseHeaders as Record<string, string>)["content-length"]
      if (cl) downloadTotal = parseInt(cl, 10)
    }
    return responseHeaders
  }

  return new Promise<Response>((resolve, reject) => {
    const xhttp = new XMLHttpRequest()
    xhttp.responseType = "arraybuffer"

    const stream = new ReadableStream<Uint8Array>({
      start(controller) {
        const trackEvents = (
          target: XMLHttpRequest | XMLHttpRequestUpload,
          fn: NetworkTrackingCallback,
          getTotal: () => number | undefined
        ) => {
          const events: Array<keyof XMLHttpRequestEventMap> = [
            "loadstart",
            "progress",
            "load",
            "loadend",
          ]
          for (const ev of events) {
            target.addEventListener(ev, evt => {
              const { total, loaded: done } = evt as ProgressEvent
              fn({
                done,
                total: getTotal() ?? (total || done),
              })
            })
          }
        }

        if (download) trackEvents(xhttp, download, () => downloadTotal)
        if (upload) trackEvents(xhttp.upload, upload, () => undefined)

        xhttp.onerror = () => {
          const error = new Error("Network error")
          controller.error(error)
          reject(error)
        }

        // Data arrives all-at-once with arraybuffer in XHR
        xhttp.onload = () => {
          controller.enqueue(new Uint8Array(xhttp.response))
          controller.close()
        }

        xhttp.onreadystatechange = () => {
          if (xhttp.readyState === xhttp.HEADERS_RECEIVED) {
            resolve(
              new Response(allowBody(xhttp.status) ? stream : null, {
                status: xhttp.status,
                statusText: xhttp.statusText,
                headers: setHeaders(xhttp),
              })
            )
          }
        }

        xhttp.open(data ? "POST" : "GET", url, true)
        xhttp.withCredentials = !!withCredentials

        new Headers(headers).forEach((value, key) =>
          xhttp.setRequestHeader(key, value)
        )

        try {
          xhttp.send(data)
        } catch (err) {
          reject(err)
        }
      },
      cancel() {
        xhttp.abort()
      },
    })
  })
}
